import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/sidebar/sidebar';
import { Flex } from '@chakra-ui/react';
import Navbar from '../components/navbar/navbar';
import WidgetDrawer from '../components/widgets/drawer';
import '../styles/content-layout-fix.css';
import { setScreen } from '../redux/slices/setScreenSlice';
import { useDispatch } from 'react-redux';
import {
  useFeatureFlagFromContext,
  useFeatureFlagsFromContext,
} from '../launchdarkly';
import { redirectIfNoIntegrations } from '../utils/integrationCheck';
import { useLocation } from 'react-router-dom';

const Dashboard = () => {
  // Check if the reskin feature flag is enabled
  // This will be used in future implementations
  const showReskin = useFeatureFlagFromContext('showReskin', false);

  // Get all feature flags to pass to child components
  const flags = useFeatureFlagsFromContext();

  // State hooks
  const [view, setView] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [costDesc, setCostDesc] = useState(null);
  const [showSidebar, setShowSidebar] = useState(
    window.matchMedia('(min-width: 1000px)').matches
  );
  const [isMobile, setIsMobile] = useState(
    window.matchMedia('(max-width: 500px)').matches
  );
  const [visibleScreen, setVisibleScreen] = useState(null);

  // Initialize sidebar and content widths with useCallback to prevent recreation on each render
  const getSidebarWidth = useCallback(collapsed => {
    // Adjust sidebar widths based on collapsed state
    if (collapsed) return '60px'; // Minimal width when collapsed

    const isSmallScreen = window.matchMedia('(max-width: 600px)').matches;
    const isMediumScreen = window.matchMedia(
      '(min-width: 601px) and (max-width: 1250px)'
    ).matches;
    const isLargeScreen = window.matchMedia('(min-width: 1250px)').matches;

    if (isSmallScreen) {
      return '60%';
    } else if (isMediumScreen) {
      return '250px';
    } else if (isLargeScreen) {
      return '20%';
    }
    return '20%';
  }, []);

  const getContentWidth = useCallback(
    showSidebarParam => {
      const isSmallScreen = window.matchMedia('(max-width: 600px)').matches;
      const isMediumScreen = window.matchMedia(
        '(min-width: 601px) and (max-width: 1250px)'
      ).matches;
      const isLargeScreen = window.matchMedia('(min-width: 1250px)').matches;

      // Get the sidebar width based on collapse state
      let sidebarWidth = getSidebarWidth(sidebarCollapsed);

      // Original logic for non-reskinned UI
      if (isSmallScreen) {
        return '100%'; // Full width for small screens
      } else if (isMediumScreen || isLargeScreen) {
        // Check if the showReskin feature flag is enabled
        if (showReskin) {
          // When showReskin is true, adjust content width to be full width minus the width of the collapsed ReskinnedSidebar
          // ReskinnedSidebar has a fixed width of 80px when collapsed
          const reskinnedSidebarWidth = '80px';
          return showSidebarParam
            ? `calc(100% - ${reskinnedSidebarWidth})`
            : '100%';
        } else {
          // Original logic for non-reskinned UI
          return showSidebarParam ? `calc(100% - ${sidebarWidth})` : '100%';
        }
      }
      return '100%';
    },
    [getSidebarWidth, sidebarCollapsed, showReskin]
  );

  const [sidebarWidth, setSidebarWidth] = useState(() =>
    getSidebarWidth(false)
  );
  const [contentWidth, setContentWidth] = useState(() =>
    getContentWidth(window.matchMedia('(min-width: 1000px)').matches)
  );

  // Redux dispatch
  const dispatch = useDispatch();
  const location = useLocation();

  // Handle window resize with useCallback to prevent recreation on each render
  const handleResize = useCallback(() => {
    const newShowSidebar = window.matchMedia('(min-width: 1000px)').matches;
    setShowSidebar(newShowSidebar);
    setIsMobile(window.matchMedia('(max-width: 500px)').matches);
    setSidebarWidth(
      sidebarCollapsed ? getSidebarWidth(true) : getSidebarWidth(false)
    );
    setContentWidth(getContentWidth(newShowSidebar));
  }, [sidebarCollapsed, getSidebarWidth, getContentWidth]);

  // Handle visible screen change
  const handleVisibleScreen = useCallback(screen => {
    setVisibleScreen(screen);
  }, []);

  // Toggle sidebar collapse
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);

  // Update sidebar and content widths when sidebarCollapsed changes
  useEffect(() => {
    setSidebarWidth(getSidebarWidth(sidebarCollapsed));
    setContentWidth(getContentWidth(showSidebar));
  }, [sidebarCollapsed, showSidebar, getSidebarWidth, getContentWidth]);

  // Add resize event listener on mount, remove on unmount
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    setSidebarWidth(getSidebarWidth(sidebarCollapsed));
    setContentWidth(getContentWidth(showSidebar));

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [
    handleResize,
    getSidebarWidth,
    getContentWidth,
    sidebarCollapsed,
    showSidebar,
  ]);

  // Check if user has integrations and redirect if needed
  useEffect(() => {
    // Check URL parameters for view
    const params = new URLSearchParams(location.search);
    const viewParam = params.get('view');

    // If view parameter exists, set it
    if (viewParam) {
      setView(viewParam);
      dispatch(setScreen(viewParam));
    } else {
      // Only check for integrations if no specific view is requested
      const checkIntegrations = async () => {
        // Check if user has integrations
        const redirected = await redirectIfNoIntegrations(showReskin);

        // If redirected, update the view state to match
        if (redirected) {
          setView('integrationSettings');
        }
      };

      checkIntegrations();
    }
  }, [location, dispatch]);

  return (
    <>
      <Flex width={'100%'}>
        {showSidebar ? (
          <Flex flex={1}>
            <Sidebar
              clickThruScreen={costDesc}
              onClick={value => {
                dispatch(setScreen(value));
                setView(value);
                setCostDesc(null);
                console.log(value);
              }}
              sidebarCollapsed={sidebarCollapsed}
              toggleSidebar={toggleSidebar} // Pass toggle function as prop
              view={view}
              sidebarWidth={sidebarWidth}
              handleVisibleScreen={handleVisibleScreen}
            />
          </Flex>
        ) : (
          <></>
        )}

        <Flex
          direction={'column'}
          width={contentWidth}
          // ml={showSidebar ? sidebarWidth : '0'}
        >
          <Flex width={'100%'}>
            <Navbar
              modeMobile={isMobile}
              clickThruScreen={costDesc}
              onClick={value => {
                setView(value);
                console.log(value);
              }}
              view={view}
            />
          </Flex>
          <Flex
            width={'100%'}
            p={0}
            className="widget-drawer-content max-width-container"
          >
            <WidgetDrawer
              highlightDesc={costDesc}
              view={view}
              modeMobile={isMobile}
              clickThru={(screen, desc) => {
                // If screen is 'cost' and showReskin is true, navigate to 'reskinnedCost' instead
                const targetScreen =
                  screen === 'cost' && showReskin ? 'reskinnedCost' : screen;
                setView(targetScreen);
                setCostDesc(desc);
                console.log(desc, 'navigating to:', targetScreen);
              }}
              sidebarWidth={sidebarWidth}
              visibleScreen={visibleScreen}
              flags={flags}
            />
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default Dashboard;
